import React, { useEffect } from "react";
import { useField } from "formik";
import { Entity } from "@common/types/entity";
import { Category } from "@common/types/category";
import { PERSONAL_CATEGORY_BUSINESS_ENTITY_ERROR } from "@common/helpers/rules";
import { useIsGlFirst } from "@hooks/useIsGlFirst";
import { TransactionRuleActionFormSchema } from "../constants";
import { hasSplitCategoryEntityConflict } from "../splitCategoryEntityValidation";
import { RuleSplitSpreadsheet } from "./RuleSplitSpreadsheet";
import styles from "./RuleSplitForm.module.scss";

interface RuleSplitFormProps {
    index: number;
    entities: Entity[];
    entitiesById: Map<number, Entity>;
    categoriesById: Record<string, Category>;
}

export const RuleSplitForm = ({
    index,
    entities,
    entitiesById,
    categoriesById,
}: RuleSplitFormProps) => {
    const [field, , helpers] = useField<
        TransactionRuleActionFormSchema["value"]
    >(`actions.${index}.value`);

    const { enabled: isGlFirst } = useIsGlFirst();

    // Enforce percentage total = 100 by auto-adjusting the last split
    useEffect(() => {
        if (field.value?.splitAction?.isPercentage) {
            const total = field.value?.splitAction?.splits?.reduce(
                (acc, split) => acc + (split.percentageAmount ?? 0),
                0,
            );
            if (total !== 100) {
                const diff =
                    100 -
                    total +
                    (field.value?.splitAction?.splits.at(-1)
                        ?.percentageAmount ?? 0);

                helpers.setValue({
                    splitAction: {
                        ...field.value.splitAction,
                        splits: field.value?.splitAction?.splits?.map(
                            (split, ind, splits) => ({
                                ...split,
                                percentageAmount:
                                    ind === splits.length - 1
                                        ? diff
                                        : split.percentageAmount,
                            }),
                        ),
                    },
                });
            }
        }
    }, [field.value, helpers]);

    const hasConflict =
        !isGlFirst &&
        hasSplitCategoryEntityConflict({
            splits: field.value?.splitAction?.splits ?? [],
            categoriesById,
            entitiesById,
        });

    return (
        <section className={styles.container}>
            <RuleSplitSpreadsheet
                index={index}
                entities={entities}
                isPercentage={!!field.value?.splitAction?.isPercentage}
                isGlFirst={isGlFirst}
            />
            {hasConflict && (
                <div className={styles.splitConflictError}>
                    {PERSONAL_CATEGORY_BUSINESS_ENTITY_ERROR}
                </div>
            )}
        </section>
    );
};
